import { useState } from 'react';
import { Plus, Check, X, ArrowRightLeft, Clock, Users, CalendarDays, AlertCircle, Trash2 } from 'lucide-react';
import apiService from '../../services/apiService';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function datesOfWeek(weekStart) {
  const start = new Date(`${weekStart}T00:00:00.000Z`);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
}

// Mirrors backend/utils/scheduleTime.js's shiftDurationHours exactly (frontend/backend
// don't share JS modules in this codebase) - treats end <= start as crossing midnight.
function shiftDurationHours(startTime, endTime) {
  const toMinutes = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const startMin = toMinutes(startTime);
  let endMin = toMinutes(endTime);
  if (endMin <= startMin) endMin += 24 * 60;
  return (endMin - startMin) / 60;
}

const DRIFT_COLORS = {
  on_target: { bg: 'rgba(34,197,94,0.15)', border: 'rgba(34,197,94,0.3)', text: '#22c55e' },
  under:     { bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.3)', text: '#f59e0b' },
  over:      { bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
};

// Smooth, continuous gradient over TOTAL hours scheduled this week (a workload/overtime
// gauge - independent of any specific client commitment, unlike DRIFT_COLORS above):
// blue (well under 40) -> green at exactly 40 -> orange at 45 -> red at 46+. Reuses the
// same four colors already used elsewhere in this app (theme primary blue, success
// green, warning orange, danger red) rather than introducing a new palette.
const BLUE = [59, 130, 246], GREEN = [34, 197, 94], ORANGE = [245, 158, 11], RED = [239, 68, 68];
const lerpChannel = (a, b, t) => Math.round(a + (b - a) * t);
const lerpParts = (a, b, t) => [lerpChannel(a[0], b[0], t), lerpChannel(a[1], b[1], t), lerpChannel(a[2], b[2], t)];
// Returns [r,g,b] rather than a pre-formatted string, so callers can build both a solid
// text color and a translucent background/border from the same channels (matching
// DRIFT_COLORS' bg/border/text style above).
function weeklyHoursColorParts(hours) {
  if (hours <= 0) return BLUE;
  if (hours < 40) return lerpParts(BLUE, GREEN, hours / 40);
  if (hours === 40) return GREEN;
  if (hours < 45) return lerpParts(GREEN, ORANGE, (hours - 40) / 5);
  if (hours < 46) return lerpParts(ORANGE, RED, hours - 45);
  return RED;
}

// Employee rows x 7 day columns. Overnight shifts (endTime <= startTime) get a small
// "+1d" marker rather than a second block spanning into the next column - the shift
// still belongs to the day it STARTS on.
export default function WeekGrid({ weekStart, weekEnd, shifts, drift, canEdit, clients, roster, onRefresh }) {
  const [assignFor, setAssignFor] = useState(null); // { employeeId, employeeName, date }
  const [moveFor, setMoveFor] = useState(null); // shift being moved
  const [pending, setPending] = useState(null); // { message, kind: 'assign'|'move' }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const dates = datesOfWeek(weekStart);

  // Rows come from the schedulable roster (so someone with zero shifts this week still
  // gets a row to add their first one), unioned with anyone who has a shift but somehow
  // isn't in that roster (e.g. a management-role person scheduled ad hoc) so no real
  // shift is ever silently hidden.
  const employeeRows = [];
  const seen = new Set();
  (roster || []).forEach(r => {
    if (!seen.has(r.id)) { seen.add(r.id); employeeRows.push({ employeeId: r.id, employeeName: r.name }); }
  });
  shifts.forEach(s => {
    if (!seen.has(s.employeeId)) {
      seen.add(s.employeeId);
      employeeRows.push({ employeeId: s.employeeId, employeeName: s.employeeName });
    }
  });
  employeeRows.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));

  // Every real ACTIVE commitment (a drift row) always shows here, regardless of whether
  // a shift exists this week - a zero-shift commitment is exactly the "uncovered" case
  // AIM's shift_coverage_gap alert (aimSchedulingAssembler.js) also surfaces, and hiding
  // it here made that alert look like it came from nowhere (found live 2026-09-11: AIM
  // flagged a real Robert Snow/Real Rail Inc commitment the grid gave zero visibility
  // into at all). Only AD-HOC clients (no formal commitment to track, so there's no
  // drift row to begin with) still need the "has a real shift" gate below - that's the
  // one case with no other way to know about the relationship at all.
  const badgesForEmployee = (employeeId) => {
    const driftRows = drift.filter(r => r.employeeId === employeeId);
    const driftClientIds = new Set(driftRows.map(r => r.clientId));
    const badges = driftRows.map(r => ({ kind: 'drift', clientId: r.clientId, ...r }));

    const adhocClientIds = new Set();
    shifts.forEach(s => {
      if (s.employeeId === employeeId && s.status !== 'cancelled' && !driftClientIds.has(s.clientId)) {
        adhocClientIds.add(s.clientId);
      }
    });
    adhocClientIds.forEach(clientId => {
      const shift = shifts.find(s => s.employeeId === employeeId && s.clientId === clientId);
      const actualHours = shifts
        .filter(s => s.employeeId === employeeId && s.clientId === clientId && s.status !== 'cancelled')
        .reduce((sum, s) => sum + shiftDurationHours(s.startTime, s.endTime), 0);
      badges.push({ kind: 'adhoc', clientId, clientName: shift?.clientName || 'Unknown client', actualHours });
    });

    return badges;
  };

  const shiftsFor = (employeeId, date) => shifts.filter(s => s.employeeId === employeeId && s.date === date);

  // Total hours scheduled this week per employee (across every client/shift, including
  // ad-hoc ones with no sourceAssignmentId) - a workload/overtime gauge, distinct from
  // the per-client committed-vs-actual drift badges above.
  const totalHoursFor = (employeeId) => shifts
    .filter(s => s.employeeId === employeeId && s.status !== 'cancelled')
    .reduce((sum, s) => sum + shiftDurationHours(s.startTime, s.endTime), 0);

  // Summary strip - aggregates data already present in `shifts`/`drift`/`employeeRows`,
  // no separate fetch.
  const summary = {
    totalHours: shifts.filter(s => s.status !== 'cancelled').reduce((sum, s) => sum + shiftDurationHours(s.startTime, s.endTime), 0),
    shiftCount: shifts.filter(s => s.status !== 'cancelled').length,
    employeeCount: employeeRows.length,
    // Only the shortfall side counts as "vacant" - an over-committed row isn't unfilled,
    // it's overstaffed, so it contributes 0 here rather than offsetting real gaps elsewhere.
    vacantHours: drift.reduce((sum, r) => sum + Math.max(0, -r.drift), 0),
  };

  const resetForms = () => { setAssignFor(null); setMoveFor(null); setPending(null); setError(null); };

  const submitAssign = async (form) => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiService.proposeAssignShift(
        parseInt(form.clientId, 10), assignFor.employeeId, assignFor.date, form.startTime, form.endTime
      );
      setPending({ message: result.message, kind: 'assign' });
    } catch (err) {
      setError(err.message || 'Failed to propose shift.');
    } finally {
      setBusy(false);
    }
  };

  const submitMove = async (form) => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiService.proposeMoveShift(moveFor.id, { date: form.date, startTime: form.startTime, endTime: form.endTime });
      setPending({ message: result.message, kind: 'move' });
    } catch (err) {
      setError(err.message || 'Failed to propose move.');
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiService.proposeCancelShift(moveFor.id);
      setPending({ message: result.message, kind: 'cancel' });
    } catch (err) {
      setError(err.message || 'Failed to propose cancellation.');
    } finally {
      setBusy(false);
    }
  };

  const respond = async (approve) => {
    setBusy(true);
    setError(null);
    try {
      await apiService.sendChatMessage(approve ? 'yes' : 'no');
      resetForms();
      onRefresh?.();
    } catch (err) {
      setError(err.message || 'Failed to respond.');
    } finally {
      setBusy(false);
    }
  };

  if (employeeRows.length === 0 && !canEdit) {
    return <p style={styles.empty}>No shifts scheduled for you this week.</p>;
  }

  return (
    <div>
      <div style={styles.summaryStrip}>
        <div style={styles.summaryTile}>
          <Clock size={16} color="#3b82f6" />
          <div><div style={styles.summaryValue}>{summary.totalHours}h</div><div style={styles.summaryLabel}>Total Hours</div></div>
        </div>
        <div style={styles.summaryTile}>
          <CalendarDays size={16} color="#8b5cf6" />
          <div><div style={styles.summaryValue}>{summary.shiftCount}</div><div style={styles.summaryLabel}>Shifts</div></div>
        </div>
        <div style={styles.summaryTile}>
          <Users size={16} color="#22c55e" />
          <div><div style={styles.summaryValue}>{summary.employeeCount}</div><div style={styles.summaryLabel}>Employees</div></div>
        </div>
        <div style={styles.summaryTile}>
          <AlertCircle size={16} color={summary.vacantHours > 0 ? '#f59e0b' : '#22c55e'} />
          <div><div style={{ ...styles.summaryValue, color: summary.vacantHours > 0 ? '#f59e0b' : '#e2e8f0' }}>{summary.vacantHours}h</div><div style={styles.summaryLabel}>Vacant / Unfilled</div></div>
        </div>
      </div>

      <div style={styles.gridWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.employeeHeaderCell}>Employee</th>
              {dates.map((d, i) => (
                <th key={d} style={styles.dayHeaderCell}>
                  {DAY_LABELS[i]}<br /><span style={styles.dayHeaderDate}>{d.slice(5)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employeeRows.map(row => {
              const rowBadges = badgesForEmployee(row.employeeId);
              const totalHours = totalHoursFor(row.employeeId);
              const [hr, hg, hb] = weeklyHoursColorParts(totalHours);
              return (
                <tr key={row.employeeId}>
                  <td style={styles.employeeCell}>
                    {row.employeeName}
                    <div
                      style={{ ...styles.totalHoursBadge, background: `rgba(${hr},${hg},${hb},0.15)`, border: `1px solid rgba(${hr},${hg},${hb},0.4)`, color: `rgb(${hr},${hg},${hb})` }}
                      title="Total hours scheduled this week, across all clients"
                    >
                      {totalHours}h this week
                    </div>
                    {rowBadges.map(b => {
                      if (b.kind === 'drift') {
                        const c = DRIFT_COLORS[b.driftStatus] || DRIFT_COLORS.on_target;
                        return (
                          <div key={b.clientId} style={{ ...styles.driftBadge, background: c.bg, border: `1px solid ${c.border}`, color: c.text }}>
                            {b.clientName}: {b.actualHours}/{b.committedHours}h
                          </div>
                        );
                      }
                      return (
                        <div key={b.clientId} style={styles.adhocBadge} title="No formal weekly-hours commitment on file for this client">
                          {b.clientName}: {b.actualHours}h
                        </div>
                      );
                    })}
                  </td>
                  {dates.map(date => (
                    <td key={date} style={styles.dayCell}>
                      {shiftsFor(row.employeeId, date).map(s => {
                        const overnight = s.endTime <= s.startTime;
                        return (
                          <div
                            key={s.id}
                            style={{ ...styles.shiftChip, ...(canEdit ? styles.shiftChipEditable : {}) }}
                            onClick={() => canEdit && setMoveFor(s)}
                            title={canEdit ? 'Click to move this shift' : undefined}
                          >
                            {s.clientName}<br />{s.startTime}–{s.endTime}{overnight ? ' +1d' : ''}
                          </div>
                        );
                      })}
                      {canEdit && (
                        <button
                          style={styles.addBtn}
                          onClick={() => setAssignFor({ employeeId: row.employeeId, employeeName: row.employeeName, date })}
                          title="Add a shift"
                        >
                          <Plus size={12} />
                        </button>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {assignFor && !pending && (
        <AssignForm
          assignFor={assignFor}
          clients={clients}
          busy={busy}
          error={error}
          onCancel={resetForms}
          onSubmit={submitAssign}
        />
      )}
      {moveFor && !pending && (
        <MoveForm
          shift={moveFor}
          busy={busy}
          error={error}
          onCancel={resetForms}
          onSubmit={submitMove}
          onCancelShift={submitCancel}
        />
      )}
      {pending && (
        <div style={styles.pendingBox}>
          <div style={styles.pendingMessage}>{pending.message}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={() => respond(true)} disabled={busy} style={styles.confirmBtn}><Check size={14} /> Confirm</button>
            <button onClick={() => respond(false)} disabled={busy} style={styles.cancelBtn}><X size={14} /> Cancel</button>
          </div>
          {error && <div style={styles.error}>{error}</div>}
        </div>
      )}
    </div>
  );
}

function AssignForm({ assignFor, clients, busy, error, onCancel, onSubmit }) {
  const [clientId, setClientId] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('16:00');
  const canSubmit = clientId && startTime && endTime;

  return (
    <div style={styles.formBox}>
      <div style={styles.formTitle}><Plus size={14} /> New shift for {assignFor.employeeName}, {assignFor.date}</div>
      <div style={styles.formRow}>
        <select value={clientId} onChange={e => setClientId(e.target.value)} style={styles.select}>
          <option value="">Choose a client...</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={styles.timeInput} />
        <span style={{ color: '#94a3b8' }}>to</span>
        <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={styles.timeInput} />
      </div>
      <div style={styles.formFooter}>
        <button onClick={onCancel} style={styles.cancelBtn}><X size={14} /> Cancel</button>
        <button onClick={() => onSubmit({ clientId, startTime, endTime })} disabled={busy || !canSubmit} style={styles.assignBtn}>Propose Shift</button>
      </div>
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

function MoveForm({ shift, busy, error, onCancel, onSubmit, onCancelShift }) {
  const [date, setDate] = useState(shift.date);
  const [startTime, setStartTime] = useState(shift.startTime);
  const [endTime, setEndTime] = useState(shift.endTime);

  return (
    <div style={styles.formBox}>
      <div style={styles.formTitle}><ArrowRightLeft size={14} /> Move or remove {shift.employeeName}'s {shift.clientName} shift</div>
      <div style={styles.formRow}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={styles.timeInput} />
        <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={styles.timeInput} />
        <span style={{ color: '#94a3b8' }}>to</span>
        <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={styles.timeInput} />
      </div>
      <div style={styles.formFooter}>
        <button onClick={onCancel} style={styles.cancelBtn}><X size={14} /> Close</button>
        <button onClick={onCancelShift} disabled={busy} style={styles.removeShiftBtn}><Trash2 size={13} /> Remove Shift</button>
        <button onClick={() => onSubmit({ date, startTime, endTime })} disabled={busy} style={styles.assignBtn}>Propose Move</button>
      </div>
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

const styles = {
  empty: { color: '#64748b', textAlign: 'center', padding: 32 },
  gridWrap: { overflowX: 'auto', width: '100%' },
  table: { borderCollapse: 'collapse', width: '100%', minWidth: 720 },
  employeeHeaderCell: { textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontSize: 12, borderBottom: '1px solid rgba(59,130,246,0.2)', minWidth: 160 },
  dayHeaderCell: { textAlign: 'center', padding: '8px 6px', color: '#94a3b8', fontSize: 12, borderBottom: '1px solid rgba(59,130,246,0.2)', minWidth: 100 },
  dayHeaderDate: { color: '#64748b', fontSize: 10 },
  employeeCell: { padding: '8px 12px', color: '#e2e8f0', fontSize: 13, fontWeight: 600, borderBottom: '1px solid rgba(59,130,246,0.08)', verticalAlign: 'top' },
  dayCell: { padding: 6, borderBottom: '1px solid rgba(59,130,246,0.08)', verticalAlign: 'top', textAlign: 'center' },
  shiftChip: { background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#e2e8f0', fontSize: 11, padding: '4px 6px', marginBottom: 4 },
  shiftChipEditable: { cursor: 'pointer' },
  driftBadge: { fontSize: 10, borderRadius: 8, padding: '2px 6px', marginTop: 4, display: 'inline-block' },
  adhocBadge: { fontSize: 10, borderRadius: 8, padding: '2px 6px', marginTop: 4, display: 'inline-block', background: 'rgba(148,163,184,0.15)', border: '1px solid rgba(148,163,184,0.3)', color: '#94a3b8' },
  summaryStrip: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  summaryTile: { display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: '10px 16px', minWidth: 140 },
  summaryValue: { color: '#e2e8f0', fontSize: 18, fontWeight: 700, lineHeight: 1.1 },
  summaryLabel: { color: '#64748b', fontSize: 11 },
  totalHoursBadge: { fontSize: 10, fontWeight: 600, borderRadius: 8, padding: '2px 6px', marginTop: 4, display: 'inline-block' },
  addBtn: { background: 'rgba(59,130,246,0.1)', border: '1px dashed rgba(59,130,246,0.3)', borderRadius: 6, color: '#3b82f6', padding: '3px 6px', cursor: 'pointer', display: 'flex', margin: '0 auto' },
  formBox: { background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: 12, marginTop: 12 },
  formTitle: { display: 'flex', alignItems: 'center', gap: 6, color: '#e2e8f0', fontSize: 13, fontWeight: 600, marginBottom: 8 },
  formRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  formFooter: { display: 'flex', gap: 8, marginTop: 10 },
  select: { padding: '6px 10px', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, color: '#e2e8f0', fontSize: 13, minWidth: 180 },
  timeInput: { padding: '6px 10px', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, color: '#e2e8f0', fontSize: 13 },
  assignBtn: { padding: '6px 14px', background: 'linear-gradient(135deg,#6C63FF,#3b82f6)', border: 'none', borderRadius: 6, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  confirmBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', fontSize: 12, cursor: 'pointer' },
  cancelBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', fontSize: 12, cursor: 'pointer' },
  removeShiftBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 6, color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  pendingBox: { background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: 12, marginTop: 12 },
  pendingMessage: { color: '#94a3b8', fontSize: 12, whiteSpace: 'pre-wrap' },
  error: { color: '#f87171', fontSize: 12, marginTop: 6 },
};
